/**
 * Calls `measure` again when the layout of a text can change without a new render: after the
 * element changes size (a window resize, a tab that opens), and after web fonts finish loading
 * (a font changes the line breaks but not the box).
 *
 * <p>The call waits for the next animation frame, so a measurement that changes the layout does
 * not trigger the observer again in the same frame. Returns the cleanup.
 */
export function observeTextLayout(element: HTMLElement, measure: () => void): () => void {
  let frame = 0;
  let open = true;
  const schedule = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => { if (open) measure(); });
  };
  const observer = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(schedule);
  observer?.observe(element);
  const fonts: FontFaceSet | undefined = document.fonts;
  void fonts?.ready.then(schedule);
  fonts?.addEventListener("loadingdone", schedule);
  return () => {
    open = false;
    cancelAnimationFrame(frame);
    observer?.disconnect();
    fonts?.removeEventListener("loadingdone", schedule);
  };
}
